import React, {
    useState, Fragment, useRef, useCallback, useMemo,
} from 'react';
import { makeStyles } from '@material-ui/styles';
import { Backdrop } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { doveGray, white } from '../constants/Colors';
import Utils from '../services/UtilsService';

const SidePanelContext = React.createContext({
    showSidePanel: () => {},
    hideSidePanel: () => {},
});

export const SidePanelProvider = SidePanelContext.Provider;

export const SidePanelConsumer = SidePanelContext.Consumer;

const HIDE_TRANSITION_TIME = 200;
const useStyles = makeStyles(() => ({
    sidePanelRoot: {
        position: ({ fullScreen }) => (fullScreen ? 'static' : 'fixed'),
        top: '0',
        right: '0',
        width: ({ isMobile, fullScreen, width }) => {
            const widthType = typeof width;

            if (widthType === 'number') {
                return `${width}px`;
            } if (widthType === 'string') {
                return width;
            }

            return (isMobile || fullScreen ? '100%' : '60%');
        },
        minWidth: ({ minWidth }) => minWidth,
        height: '100%',
        opacity: '1',
        zIndex: 107,
        backgroundColor: white,
        overflow: 'auto',
        display: 'flex',
        animationName: ({ fullScreen }) => (fullScreen ? '' : 'samplePreviewIn'),
        animationDuration: ({ fullScreen }) => (fullScreen ? '' : '.40s'),
        animationTimingFunction: ({ fullScreen }) => (fullScreen ? '' : 'easy'),
        animationFillMode: ({ fullScreen }) => (fullScreen ? '' : 'backwards'),
        animationDelay: ({ fullScreen }) => (fullScreen ? '' : '5ms'),
    },
    container: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
    },
    closeButton: {
        position: 'absolute',
        right: '0',
        top: '0',
        zIndex: 1,

        '& svg': {
            color: doveGray,
        },
    },
    sidePanel: {
        '&.overflow': {
            overflow: 'initial',
        },
    },
    backdrop: {
        zIndex: 6,
    },
}));

const SidePanelItem = (props) => {
    const {
        id,
        body,
        open,
        className,
        width,
        minWidth,
        closeButtonClassName = '',
        hasCloseButton = true,
        onClose,
    } = props;

    const classes = useStyles({ width, minWidth });
    const rootClasses = Utils.getClassName([
        classes.sidePanelRoot,
        className,
    ]);

    return (
        <Fragment>
            <div className={rootClasses} key={id}>
                { body }
                { hasCloseButton && <div className={closeButtonClassName || classes.closeButton}>
                    <IconButton
                        aria-label='close'
                        onClick={() => onClose(id)}>
                        <CloseIcon/>
                    </IconButton>
                </div> }
            </div>
            <Backdrop
                className={classes.backdrop}
                open={open}
                transitionDuration={400}
                onClick={() => {
                    if (body?.props?.onCloseButtonClick) {
                        body.props.onCloseButtonClick();
                    }
                    onClose(id);
                }}
            />
        </Fragment>
    );
};

const SidePanelPlaceholder = (props) => {
    const { sidePanels, onClose } = props;

    return (sidePanels || []).map((sidePanelProps) => <SidePanelItem {...sidePanelProps} onClose={onClose} key={props.id}/>);
};

export const AppWithSidePanel = ({ children }) => {
    const [sidePanels, setSidePanels] = useState([]);
    const currentSidePanelId = useRef(1);

    const removeSidePanel = useCallback((sidePanelId) => {
        if (!sidePanelId) {
            throw new Error('Id should be provided for side panel');
        }

        setSidePanels((currentSidePanels) => (currentSidePanels || []).filter(({ id }) => id !== sidePanelId));
    }, []);

    const hideSidePanel = useCallback((sidePanelId) => {
        setSidePanels((currentSidePanels) => {
            const index = (currentSidePanels || []).findIndex(({ id }) => id === sidePanelId);

            if (index > -1) {
                return ((currentSidePanels || []).map((currentSidePanel) => {
                    if (currentSidePanel.id === sidePanelId) {
                        return {
                            ...currentSidePanel,
                            open: false,
                        };
                    }

                    return currentSidePanel;
                }));
            }

            return currentSidePanels;
        });

        setTimeout(() => removeSidePanel(sidePanelId), HIDE_TRANSITION_TIME);
    }, [removeSidePanel]);

    const showSidePanel = useCallback((sidePanel) => {
        const id = currentSidePanelId.current;

        setSidePanels((prevState) => [...prevState, { ...sidePanel, open: true, id }]);

        currentSidePanelId.current += 1;

        return id;
    }, []);

    const contextValue = useMemo(() => ({
        showSidePanel,
        hideSidePanel,
    }), [
        showSidePanel,
        hideSidePanel,
    ]);

    const onClose = useCallback((id) => hideSidePanel(id), [hideSidePanel]);

    return (
        <SidePanelProvider value={contextValue}>
            <SidePanelPlaceholder
                sidePanels={sidePanels}
                onClose={onClose}
            />
            {children}
        </SidePanelProvider>
    );
};

export default SidePanelContext;
